import { useEffect } from "react";
import { isTextInput } from "@/lib/is-text-input";
import { KEYBOARD_SCROLL_CONTAINER_SELECTOR } from "@/lib/overlay-keyboard-layout";

function prefersNativeKeyboardInsets(): boolean {
  return window.matchMedia("(pointer: coarse)").matches;
}

function scrollFocusedInputInContainer(target: Element, scrollContainer: HTMLElement) {
  const targetRect = target.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  const relativeTop = targetRect.top - containerRect.top + scrollContainer.scrollTop;
  const isDrawer = scrollContainer.hasAttribute("data-drawer-scroll");
  const visibleFraction = isDrawer ? 4 : 3;
  const centerOffset = relativeTop - containerRect.height / visibleFraction;
  scrollContainer.scrollTo({ top: Math.max(0, centerOffset), behavior: "auto" });
}

function scrollContainerMovesWithKeyboard(scrollContainer: HTMLElement): boolean {
  return (
    scrollContainer.hasAttribute("data-drawer-scroll") ||
    scrollContainer.hasAttribute("data-keyboard-scroll")
  );
}

/**
 * Scrolls focused inputs within app-managed scroll containers on touch devices only.
 * Skips desktop — auto-scroll there pans the page and feels disruptive.
 */
function useAutoScrollInput() {
  useEffect(() => {
    function onFocusIn(e: FocusEvent) {
      if (!prefersNativeKeyboardInsets()) return;

      const target = e.target;
      if (!(target instanceof Element) || !isTextInput(target)) return;

      const scrollContainer = target.closest(KEYBOARD_SCROLL_CONTAINER_SELECTOR);
      if (!(scrollContainer instanceof HTMLElement)) return;
      const movesWithKeyboard = scrollContainerMovesWithKeyboard(scrollContainer);

      const scrollInContainer = () => {
        if (document.activeElement !== target) return;
        scrollFocusedInputInContainer(target, scrollContainer);
      };

      const runAfterLayout = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(scrollInContainer);
        });
      };

      if (movesWithKeyboard) {
        setTimeout(runAfterLayout, 150);
      } else {
        runAfterLayout();
      }

      if (!movesWithKeyboard) return;

      const onViewportChange = () => {
        if (document.activeElement !== target) return;
        runAfterLayout();
      };

      window.visualViewport?.addEventListener("resize", onViewportChange);
      window.visualViewport?.addEventListener("scroll", onViewportChange);

      const cleanupViewport = () => {
        window.visualViewport?.removeEventListener("resize", onViewportChange);
        window.visualViewport?.removeEventListener("scroll", onViewportChange);
      };

      target.addEventListener("blur", cleanupViewport, { once: true });
    }

    document.addEventListener("focusin", onFocusIn, { passive: true });
    return () => document.removeEventListener("focusin", onFocusIn);
  }, []);
}

export { useAutoScrollInput };
